import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Divider
} from '@mui/material';
import { withRouter } from "react-router";
import { getUserId, getUserObject } from "../../../selectors/UserSelectors";
import { SUMMARY_TITLE_FONT } from "../../../constants/Styles";
import { LoadingButton } from "../../common/StyledButtons";
import StyledCheckbox from "../../common/StyledCheckbox";
import { getPolicyFromPolicyType, updatePolicyAcceptance, validatePolicyAcceptance } from "../../../actions/internal/LegalActions";
import { getAllSites } from "../../../selectors/SiteSelectors";
import { StyledLoadingSection } from "../../common/StyledProgressCircle";

const UserReviewPoliciesPage = ({ history, location }) => {

  const [ hasAgreed, setHasAgreed ] = useState(false);
  const [ isSubmittingAgreement, setIsSubmittingAgreement ] = useState(false);
  const [ policyCheck, setPolicyCheck ] = useState();
  const [ policyList, setPolicyList ] = useState();
  const [ pollicyIndex, setPolicyIndex ] = useState(0);

  useEffect(() => {
    setupPolicies();
  }, [])

  const setupPolicies = () => {
    const pc = validatePolicies();
    if (!pc || !pc.policies) {
      history.replace('/dashboard');
    } else {
      setPolicyCheck(pc);
      setPolicyList(pc.policies);
    }
  }

  const handleCheckboxChange = (e) => {
    setHasAgreed(e.target.checked);
  }

  const validatePolicies = () => {
    const user = getUserObject();
    const sites = getAllSites();

    const policies = validatePolicyAcceptance(user, sites);
    if (policies.length > 0) {
        const redirect = location.pathname;
        return { redirect, policies }
    }
  }

  const getBusinessName = () => {
    const { site_id } = policyList[pollicyIndex];
    const sites = getAllSites();
    const site = sites.find(site => site.site_id === site_id);
    if (!site) return;
    return site.site_details.site_name;
  }

  const handleSubmitPolicyAgreement = async (policy) => {
    try {
      setIsSubmittingAgreement(true);
      const { version, type } = policy;
      const { site_id } = policyList[pollicyIndex];
      const accepted_timestamp = Date.now();
      const userId = getUserId();
      const resp = await updatePolicyAcceptance(site_id, userId, { version, type, accepted_timestamp });
      incrementPolicyOrExit();
    } catch (ex) {
      console.error(ex);
    } finally {
      setIsSubmittingAgreement(false);
    }
  }

  const incrementPolicyOrExit = () => {
    const shouldExit = pollicyIndex === policyList.length - 1;
    if (shouldExit && !isUserSetup()) {
      history.replace('/account/setup');
    } else if (shouldExit) {
      const redirect = !policyCheck.redirect || policyCheck.redirect === '/account/review' ? '/dashboard' : policyCheck.redirect;
      history.replace(redirect);
    } else {
      setPolicyIndex(pollicyIndex + 1);
      setHasAgreed(false);
    }
  }

  const isUserSetup = () => {
    const user = getUserObject();
    if (!user || !user.metadata || !user.metadata.dashboard_setup_timestamp) return false;
    return true;
  }

  const renderPolicyMarkup = (markup) => {
    return (
      <div
        style={{
          fontSize: '14px',
          lineHeight: '1.6',
        }}
      >
        <style>
          {`
            .policy-content h1 { font-size: 20px; margin-top: 1.5em; }
            .policy-content h2 { font-size: 18px; margin-top: 1.25em; }
            .policy-content h3 { font-size: 16px; margin-top: 1em; }
            .policy-content h4 { font-size: 15px; margin-top: 0.75em; }
            .policy-content ul { padding-left: 1.25em; }
            .policy-content p { margin-bottom: 1em; }
          `}
        </style>
        <div
          className="policy-content"
          dangerouslySetInnerHTML={{ __html: markup }}
        />
      </div>
    );
  };

  const renderPolicySection = (policy = {}) => (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Paper
          variant="outlined"
          sx={{
            marginTop: '30px',
            overflow: 'hidden',
            padding: '24px',
          }}
        >
          {/* Title & Description Section */}
          <Box sx={{ marginBottom: 3 }}>
            <h4 style={{ ...SUMMARY_TITLE_FONT, textAlign:'center', paddingBottom: 0, marginBottom: '10px', fontSize: '24px' }}>
              Review & Accept
            </h4>
            <p style={{ textAlign: 'center', paddingTop: 0, marginTop: 0, fontSize: '14px', paddingLeft: '10%', paddingRight: '10%' }}>
              Please take a moment to carefully review the information below. By proceeding, you acknowledge that you have read, understood, and agree to the contents provided. { policyList[pollicyIndex].site_id ? ` This acknowledgment applies on behalf of your business, ${getBusinessName()}.` : ''} Your acceptance is required to continue using BCK services. If you have any questions or concerns, please contact us at <a href="mailto:team@consultwithkyle.com">team@consultwithkyle.com</a> before accepting.
            </p>
          </Box>

          <Divider />

          {/* Policy Title */}
          <Box sx={{ marginBottom: 2, marginTop: 2 }}>
            <Typography sx={{ ...SUMMARY_TITLE_FONT, fontSize: '20px' }} align="center">
              {policy.title}
            </Typography>
          </Box>

          {/* Policy Content */}
          <Box
            sx={{
              maxHeight: '500px',
              overflowY: 'auto',
              padding: '16px',
              backgroundColor: '#fafafa',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
            }}
          >
            {renderPolicyMarkup(policy.markup)}
          </Box>
          <Box sx={{ width: '100%', marginTop: 2 }}>
            <StyledCheckbox checked={hasAgreed} label="I have reviewed and accept the terms outlined in the policy shown above." onChange={handleCheckboxChange} /> 
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', width: '100%', marginTop: 4 }}>
            <LoadingButton disabled={!hasAgreed} isLoading={isSubmittingAgreement} onClick={() => handleSubmitPolicyAgreement(policy)} >Continue</LoadingButton>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  const loadingContent = (
    <StyledLoadingSection />
)

  return (
    <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
      <Box
        sx={{
          maxWidth: '840px',
          margin: '0 auto',
          width: '100%',
          padding: 3,
        }}
      >
        {!policyList ? loadingContent : renderPolicySection(getPolicyFromPolicyType(policyList[pollicyIndex].policy_type))}
      </Box>
    </Box>
  );
};

export default withRouter(UserReviewPoliciesPage);
